import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import {
  contractStatusSchema,
  contractStatusMap,
  propertyTypeSchema,
  propertyTypeMap,
} from 'src/shared/schemas/referrals'
import {
  attorneyTransactionTypeMap,
  attorneyTransactionTypeSchema,
} from 'src/shared/schemas/referrals/referral-fields/attorney/attorney-transaction-type'
import { timeframeMap, timeframeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import { useEventStore } from 'src/shared/store/event-store'

const attorneySchema = z.object({
  attorneyTransactionType: attorneyTransactionTypeSchema,
  propertyType: propertyTypeSchema,
  contractStatus: contractStatusSchema,
  // Optional fields
  timeframe: timeframeSchema.optional(),
})

type AttorneyFormData = z.infer<typeof attorneySchema>

export const AttorneyInfo: React.FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const form = useForm<AttorneyFormData>({
    resolver: zodResolver(attorneySchema),
    mode: 'onSubmit',
    defaultValues: {
      attorneyTransactionType:
        referralFormData.attorneyTransactionType || selectedReferral?.attorneyTransactionType || undefined,
      propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      contractStatus: referralFormData.contractStatus || selectedReferral?.contractStatus || undefined,
      timeframe: referralFormData.timeframe || selectedReferral?.timeframe || undefined,
    },
  })

  const onSubmit = async (data: AttorneyFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_attorney_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-8">
        <SelectFormField
          control={form.control}
          name="attorneyTransactionType"
          label="Transaction Type"
          options={attorneyTransactionTypeMap}
          required
        />
        <SelectFormField
          control={form.control}
          name="propertyType"
          label="Property Type"
          options={propertyTypeMap}
          required
        />
        <SelectFormField
          control={form.control}
          name="contractStatus"
          label="Contract Status"
          options={contractStatusMap}
          required
        />
        <SelectFormField control={form.control} name="timeframe" label="Timeline" options={timeframeMap} required />

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
